import { useContext, createContext } from 'react';
import { Helmet } from 'react-helmet-async';
import qs from 'qs';

import { Market, MarketInfo, MarketInfoProps } from 'etc/types';

import { MARKET_DATA } from './marketConfig';
import { LOCALES } from './localeConfig';

const MARKET_REGEX = /^\/(\w{2}(?:-\w{2})?)\//; // for instance, ca-fr
const DEFAULT_MARKET = 'us';

const softLaunchOverride: {
  market: { [key: string]: Market };
  dir?: string;
} = {
  market: {},
  dir: undefined,
};

const getMarketData = (market: string) => softLaunchOverride.market[market] || MARKET_DATA[market];

export const MarketInfoContext = createContext<MarketInfo>({} as MarketInfo);
export const useMarketInfo = () => useContext(MarketInfoContext);

export const withMarketInfo = <P extends MarketInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof MarketInfoProps>) => (
    <MarketInfoContext.Consumer>
      {marketInfo => <Comp {...(props as P)} marketInfo={marketInfo} />}
    </MarketInfoContext.Consumer>
  );
};

export const getLocaleForMarket = (market: string) => getMarketData(market).locale;
export const getMastheadDirOverride = (market: string) => getMarketData(market).mastheadDirOverride;

const setupMarketOverride = () => {
  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  if (!queryParams.softlaunch) {
    return;
  }

  const [market, locale = market, dir = 'ltr'] = (queryParams.softlaunch as string).split(':');

  softLaunchOverride.market = {
    [market]: {
      locale,
      country: market,
    },
  };
  softLaunchOverride.dir = dir;
};

const getMarketFromUrl = () => {
  const regMarket = window.location.pathname.match(MARKET_REGEX);

  if (regMarket && regMarket[1] && getMarketData(regMarket[1])) {
    return regMarket[1];
  }
  return DEFAULT_MARKET;
};

export const initiateMarket = () => {
  setupMarketOverride();
  return getMarketFromUrl();
};

interface MarketInfoProviderProps {
  market: string;
  children: React.ReactNode;
}

const MarketInfoProvider = ({ market, children }: MarketInfoProviderProps) => {
  const contextValue: MarketInfo = {
    market,
    phonePrefix: '',
    ...getMarketData(market),
  };

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: contextValue.locale,
          // The locales file is a special case since it is .js used from Gulp for translation syncs
          dir: softLaunchOverride.dir || (LOCALES as any)[contextValue.locale].dir,
        }}
      />

      <MarketInfoContext.Provider value={contextValue}>{children}</MarketInfoContext.Provider>
    </>
  );
};

export default MarketInfoProvider;
